import * as React from 'react';
import {
  PopoverContent,
  SearchableList,
  SearchableListBox,
  SearchableListInput,
  Divider,
  View,
  SearchDefaultIcon,
  Popover,
  PopoverTrigger,
  SelectTrigger,
  OptionType,
} from '@packages/systems/spring';
import {SEARCHABLE_LIST_BOX_VIRTUALIZED_MAX_HEIGHT} from '@packages/systems/design-system/constants/DesignerUI';
import {PageOption} from './PageOption';
import type {ExtraPageOptionType, PageOptionType} from './types';

type PageSwitcherPopoverProps = {
  /**
   * The currently selected page value.
   */
  value: string | null | undefined;
  /**
   * List of available page options to select from.
   * Each option should have a label and value, and can optionally include a prefix icon.
   */
  options: PageOptionType[];
  /**
   * Callback function to handle page selection.
   */
  handlePageSelectorClick: (id: string) => void;
};

/**
 * Renders a SearchableList within a popover for selecting a page.
 */
export const PageSelectorPopover = ({
  value,
  options,
  handlePageSelectorClick,
}: PageSwitcherPopoverProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      width="300px"
      minHeight="fit-content"
      // needs to escape the viewport or it will be cut off when rendered in the right sidebar
      shouldEscapeViewport
    >
      <PopoverTrigger>
        <SelectTrigger
          data-automation-id="page-selector-button"
          id="page-selector-button"
          isOpen={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          shouldTruncateButtonText
        >
          {options.find((page) => page.id === value)?.name ||
            'Choose a page...'}
        </SelectTrigger>
      </PopoverTrigger>
      <PageSelectorContent
        value={value}
        options={options}
        handlePageSelectorClick={(val) => {
          handlePageSelectorClick(val);
          setIsOpen(false);
        }}
      />
    </Popover>
  );
};

const PageSelectorContent = ({
  value,
  options,
  handlePageSelectorClick,
}: PageSwitcherPopoverProps) => {
  /**
   * Custom option renderer so that we can add colors and icons.
   */
  const handleRenderOption = React.useCallback(
    (option: OptionType & ExtraPageOptionType) => (
      <PageOption option={option} />
    ),
    []
  );

  const memoizedOptions = React.useMemo(() => {
    return options.map((option) => ({
      ...option,
      label: option.name,
      value: option.id,
    }));
  }, [options]);

  return (
    <PopoverContent
      isSeamless
      data-automation-id="page-selector-popover"
      colorVariant="tertiary"
    >
      <SearchableList
        checkmarkStyle="icon"
        onChange={handlePageSelectorClick}
        options={memoizedOptions}
        value={value || undefined}
        selectionMode="single"
        renderOption={handleRenderOption}
      >
        <View padding="$2" borderBottom="$border1">
          <SearchableListInput
            placeholder="Search pages"
            prefix={<SearchDefaultIcon />}
            data-automation-id="page-selector-search"
          />
        </View>
        <SearchableListBox
          isVirtualized
          maxHeight={SEARCHABLE_LIST_BOX_VIRTUALIZED_MAX_HEIGHT}
          hasGroupDividers
        />
      </SearchableList>
      <Divider />
    </PopoverContent>
  );
};
